import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaFile, FaLaptop } from "react-icons/fa";
import { LiaStarSolid } from "react-icons/lia";
import { BiArrowBack } from "react-icons/bi";
import { useNavigate, useParams, NavLink } from "react-router-dom";
import Background from "../../../assets/background.png";
import axios from "axios";
import apiKey from "../../../config/apiKey";
import { toast } from "react-toastify";
import { IoChevronDownOutline, IoChevronUp } from "react-icons/io5";
import { FaFacebook, FaWhatsapp, FaLinkedin, FaLink, FaPaperPlane } from "react-icons/fa";

const TeacherPortfolio = () => {
  const { t } = useTranslation();
  const student = JSON.parse(localStorage.getItem("edo-student"));
  const navigate = useNavigate();
  const { id } = useParams();

  // data
  const [teacherDetails, setTeacherDetails] = useState(null);
  const [bySubjectsData, setBySubjectsData] = useState({});
  
  const [bySkillData, setBySkillData] = useState([]);
  const [byTimeSlotData, setByTimeSlotData] = useState([]);
  const [demoDuration, setDemoDuration] = useState([]);

  // ui states
  const [loading, setLoading] = useState(true);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [visibleCount, setVisibleCount] = useState(4);
  const [showAll, setShowAll] = useState(false);
  const [expandedClasses, setExpandedClasses] = useState({});

  // request state
  const user = { id: student ? student.student.id : "" };
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [currentid, setCurrentId] = useState(null);
  const [isAlwaysAvailable, setIsAlwaysAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [isValid, setIsValid] = useState(false);

  const [formData, setFormData] = useState({
    teacher_id: id,
    student_id: user.id,
    type_id: 2,
    timeslot_id: "",
    session_id: "",
    payment: "",
    topic: "",
  });

  // skeleton helper
  const Skeleton = ({ className = "" }) => (
    <div className={`animate-pulse bg-gray-200/60 rounded-2xl ${className}`} />
  );

  // helpers
  const formatTime = (time) => {
    if (!time) return t("studentDash.teacherProtfolio.notAvailable");
    const [h, m] = time.split(":");
    const hour = parseInt(h || "0", 10);
    const suffix = hour >= 12 ? "PM" : "AM";
    const formattedHour = (hour % 12) || 12;
    return `${formattedHour}:${m ?? "00"} ${suffix}`;
  };

  const toggleClass = (className) => {
    setExpandedClasses((p) => ({ ...p, [className]: !p[className] }));
  };

  const handleToggleView = () => {
    if (showAll) setVisibleCount(4);
    else setVisibleCount(byTimeSlotData.length);
    setShowAll((p) => !p);
  };

  const handleShare = (platform) => {
    const profileUrl = `https://edolutions.com/Teacherprof/${id}`;
    let shareUrl = "";
    switch (platform) {
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(profileUrl)}`;
        break;
      case "whatsapp":
        shareUrl = `https://wa.me/?text=${encodeURIComponent(profileUrl)}`;
        break;
      case "linkedin":
        shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(profileUrl)}`;
        break;
      default:
        return;
    }
    window.open(shareUrl, "_blank");
  };

  const handleInstagramShare = () => {
    const profileUrl = `https://edolutions.com/Teacherprof/${id}`;
    navigator.clipboard.writeText(profileUrl);
    toast.success(t("studentDash.homeTeacherPortfolio.profileLinkCopied"), {
      position: "bottom-right",
      autoClose: 3000,
      theme: "colored",
      style: { backgroundColor: "#2A3A54", color: "#fff" },
    });
  };

  // api: teacher details
  useEffect(() => {
    setLoading(true);
    axios
      .get(`${apiKey}/api/edolutions/student/teacherdetail/${id}`, {
        headers: { Authorization: `Bearer ${student?.token}` },
      })
      .then((res) => {
        setTeacherDetails(res.data.teacherDetails);
        setBySubjectsData(res.data.bySubjectsData || {});
        setBySkillData(res.data.skillsData || []);
        setByTimeSlotData(res.data.timeSlotData || []);
        setIsAlwaysAvailable((res.data.timeSlotData || []).length === 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [student?.token, id]);

  // api: demo durations
  useEffect(() => {
    axios
      .get(`${apiKey}/api/edolutions/student/demoduration`, {
        headers: { Authorization: `Bearer ${student?.token}` },
      })
      .then((res) => setDemoDuration(res.data || []))
      .catch(() => {});
  }, [student?.token]);

  // form handlers
  const handleDurationChange = async (e) => {
    const { value } = e.target;
    const selected = demoDuration.find((d) => d.id === parseInt(value));
    let payment = "";
    if (selected && teacherDetails) {
      payment = selected.days * teacherDetails.online_charges;
    }
    setSelectedDuration(selected);

    // validate for id === 5
    if (selected?.id === 5) {
      try {
        const resp = await axios.post(
          `${apiKey}/api/edolutions/student/validate-demo-request`,
          { student_id: user.id, teacher_id: id, session_id: selected.id },
          { headers: { Authorization: `Bearer ${student?.token}` } }
        );
        if (resp.status === 200) {
          setErrorMessage("");
          setIsValid(true);
          setFormData((p) => ({ ...p, session_id: value, payment }));
        }
      } catch (error) {
        if (error.response?.status === 400) {
          setErrorMessage(error.response.data.message || t("studentDash.teacherProtfolio.validationFailed"));
          setIsValid(false);
        } else {
          setErrorMessage(t("studentDash.teacherProtfolio.unexpectedError"));
        }
      }
    } else {
      setErrorMessage("");
      setFormData((p) => ({ ...p, session_id: value, payment }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target || {};
    setFormData((p) => ({ ...p, [name]: value }));
  };

  // submit / delete request
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const resp = await axios.post(
        `${apiKey}/api/edolutions/student/teacherrequest`,
        formData,
        { headers: { Authorization: `Bearer ${student?.token}` } }
      );
      const requestId = resp.data.request_id;
      setCurrentId(requestId);
      setRequestSubmitted(true);
      toast.success(resp.data.message, {
        position: "bottom-right",
        autoClose: 3500,
        theme: "colored",
        style: { backgroundColor: "#2A3A54", color: "#fff" },
      });
      setFormData({
        teacher_id: id,
        student_id: user.id,
        type_id: 2,
        timeslot_id: "",
        session_id: "",
        payment: "",
        topic: "",
      });
      setShowModal(false);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        toast.error(t("studentDash.homeTeacherPortfolio.errorSubmittingForm"), {
          position: "bottom-right",
          autoClose: 3500,
          theme: "colored",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    try {
      const resp = await axios.delete(
        `${apiKey}/api/edolutions/student/teacherrequestdelete/${currentid}`,
        { headers: { Authorization: `Bearer ${student?.token}` } }
      );
      setRequestSubmitted(false);
      toast.success(resp.data.message, {
        position: "bottom-right",
        autoClose: 3500,
        theme: "colored",
        style: { backgroundColor: "#2A3A54", color: "#fff" },
      });
    } catch {
      toast.error("Error submitting the form", {
        position: "bottom-right",
        autoClose: 3500,
        theme: "colored",
      });
    }
  };

  return (
    <div className="m-3 flex flex-col break-words max-sm:w-[95%] max-sm:mx-auto w-full mb-4 shadow-lg bg-white rounded-3xl border border-gray-100">
      {/* HERO */}
      <div className="relative overflow-hidden rounded-3xl">
        <div
          className="h-48 sm:h-56 w-full bg-cover bg-center"
          style={{ backgroundImage: `url(${Background})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/20 to-transparent" />
        <div className="absolute inset-0 flex items-end">
          <div className="p-5 sm:p-8 w-full flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            {/* left side: avatar + name */}
            <div className="flex items-center gap-4">
              {loading ? (
                <Skeleton className="h-24 w-24 rounded-full border-4 border-white" />
              ) : (
                <img
                  src={
                    teacherDetails?.photo
                      ? `${apiKey}/userProfile/${teacherDetails.photo}`
                      : `${apiKey}/userProfile/87654321.png`
                  }
                  alt={teacherDetails?.name || t("studentDash.teacherProtfolio.teacherProfileAlt")}
                  className="h-24 w-24 rounded-full border-4 border-white object-cover shadow-md"
                />
              )}
              <div className="text-white">
                <div
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-2 text-sm opacity-80 hover:opacity-100 cursor-pointer"
                >
                  <BiArrowBack /> {t("studentDash.common.back")}
                </div>
                <h1 className="text-2xl sm:text-3xl font-extrabold mt-1 capitalize">
                  {teacherDetails?.user_name || (loading ? t("studentDash.common.loadingEllipsis") : t("studentDash.teacherProtfolio.unnamedTeacher"))}
                </h1>
                <p className="text-sm opacity-90">
                  {teacherDetails?.online_charges
                    ? t("studentDash.homeTeacherPortfolio.perHourRate", { amount: teacherDetails.online_charges })
                    : "—"}
                </p>
              </div>
            </div>

            {/* right side: actions */}
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end">
              {requestSubmitted ? (
                <button
                  className="px-5 py-2 rounded-xl text-white font-semibold bg-emerald-500 hover:bg-emerald-600"
                  onClick={handleDelete}
                >
                  {t("studentDash.homeTeacherPortfolio.cancelRequest")}
                </button>
              ) : (
                <button
                  className="px-5 py-2 rounded-xl text-white font-semibold bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] hover:from-[#2A3A54] hover:to-[#EE5F2B]"
                  onClick={() => setShowModal(true)}
                >
                  {t("studentDash.homeTeacherPortfolio.requestToApply")}
                </button>
              )}

              <button
                className="px-4 py-2 rounded-xl bg-white/90 text-gray-800 font-semibold shadow hover:bg-white flex items-center gap-2"
                onClick={() => setIsShareModalOpen(true)}
              >
                <span>{t("studentDash.homeTeacherPortfolio.shareProfile")}</span>
                <FaPaperPlane className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-5 sm:p-8">
        {/* LEFT: About, Education, Job */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-3xl border border-gray-100 bg-white shadow-sm p-6">
            <h2 className="text-xl font-bold mb-3">{t("studentDash.homeTeacherPortfolio.about")}</h2>
            {loading ? (
              <Skeleton className="h-20 w-full" />
            ) : (
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {teacherDetails?.bio || t("studentDash.teacherProtfolio.noBioProvided")}
              </p>
            )}
          </div>

          <div className="rounded-3xl border border-gray-100 bg-white shadow-sm p-6">
            <h2 className="text-xl font-bold mb-3">{t("studentDash.homeTeacherPortfolio.education")}</h2>
            {loading ? (
              <Skeleton className="h-24 w-full" />
            ) : (
              <div className="space-y-1 text-gray-700">
                <p className="font-semibold">{teacherDetails?.degree || "—"}</p>
                <p>{teacherDetails?.school || "—"}</p>
                <p>{t("studentDash.homeTeacherPortfolio.graduationYear", { year: teacherDetails?.graduation_year || "—" })}</p>
              </div>
            )}
          </div>

          <div className="rounded-3xl border border-gray-100 bg-white shadow-sm p-6">
            <h2 className="text-xl font-bold mb-3">{t("studentDash.homeTeacherPortfolio.job")}</h2>
            {loading ? (
              <Skeleton className="h-28 w-full" />
            ) : (
              <div className="space-y-1 text-gray-700">
                <p className="font-semibold">{teacherDetails?.title || "—"}</p>
                <p>{teacherDetails?.company || "—"}</p>
                <p>
                  {t("studentDash.teacherProtfolio.startLabel", { month: teacherDetails?.start_month || "—", year: teacherDetails?.start_year || "" })}
                </p>
                <p>
                  {t("studentDash.teacherProtfolio.endLabel", { month: teacherDetails?.end_month || "—", year: teacherDetails?.end_year || "" })}
                </p>
                <p className="text-sm text-gray-600">
                  {teacherDetails?.description ? teacherDetails.description : "—"}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Availability, Skills, Overview, Subjects */}
        <div className="space-y-6">
          <div className="rounded-3xl border border-gray-100 bg-white shadow-sm p-6">
            <h2 className="text-xl font-bold mb-3">{t("studentDash.teacherProtfolio.availability")}</h2>
            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-5 w-2/3" />
                <Skeleton className="h-5 w-1/2" />
                <Skeleton className="h-5 w-3/4" />
              </div>
            ) : byTimeSlotData?.length ? (
              <>
                <div className="space-y-3">
                  {byTimeSlotData.slice(0, visibleCount).map((slot) => (
                    <div
                      key={slot.id}
                      className="flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2"
                    >
                      <span className="text-sm font-semibold capitalize">{slot.day_name}</span>
                      <span className="text-xs text-gray-600">
                        {formatTime(slot.start_time)} — {formatTime(slot.end_time)}
                      </span>
                    </div>
                  ))}
                </div>
                {byTimeSlotData.length > 4 && (
                  <button
                    onClick={handleToggleView}
                    className="mt-3 text-sm font-semibold text-[#2DD0A5] hover:underline"
                  >
                    {showAll ? t("studentDash.teacherProtfolio.showLess") : t("studentDash.teacherProtfolio.viewMore")}
                  </button>
                )}
              </>
            ) : (
              <p className="text-gray-600">{t("studentDash.teacherProtfolio.noTimeSlots")}</p>
            )}
          </div>

          <div className="rounded-3xl border border-gray-100 bg-white shadow-sm p-6">
            <h2 className="text-xl font-bold mb-3">{t("studentDash.homeTeacherPortfolio.skills")}</h2>
            {loading ? (
              <div className="flex flex-wrap gap-2">
                <Skeleton className="h-7 w-20" />
                <Skeleton className="h-7 w-16" />
                <Skeleton className="h-7 w-24" />
              </div>
            ) : bySkillData?.length ? (
              <div className="flex flex-wrap gap-2">
                {bySkillData.map((s, i) => (
                  <span
                    key={`${s?.id || i}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200"
                  >
                    {s?.skill_name || "—"}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-600">{t("studentDash.teacherProtfolio.noSkillsAvailable")}</p>
            )}
          </div>

          <div className="rounded-3xl border border-gray-100 bg-white shadow-sm p-6">
            <h2 className="text-xl font-bold mb-3">{t("studentDash.homeTeacherPortfolio.profileOverview")}</h2>
            <div className="flex items-center gap-1 text-yellow-400 mb-3">
              <LiaStarSolid /><LiaStarSolid /><LiaStarSolid /><LiaStarSolid /><LiaStarSolid />
              <span className="text-sm text-gray-800 ml-1">5.0</span>
            </div>
            <div className="flex items-center gap-2 text-gray-800 font-semibold mb-1">
              <FaLaptop className="text-gray-700" />
              <span>{t("studentDash.homeTeacherPortfolio.homeTuitionLocation")}</span>
            </div>
            <p className="text-sm text-gray-700">
              {teacherDetails
                ? `${teacherDetails?.city || ""}${teacherDetails?.city ? ", " : ""}${teacherDetails?.country || ""}` || "—"
                : "—"}
            </p>
          </div>

          <div className="rounded-3xl border border-gray-100 bg-white shadow-sm p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-bold">{t("studentDash.teacherProtfolio.specializationsHeading")}</h2>
              {/* <NavLink
                to={`/mentees/mentor-portfolio-2`} // change if you have a specific edit route
                className="text-sm text-orange-600 font-semibold hover:underline"
              >
                View more
              </NavLink> */}
            </div>

            <div className="flex items-center gap-2 text-gray-900 font-semibold mb-2">
              <FaFile className="text-gray-700" />
              <span>{t("studentDash.teacherProtfolio.mainProfessionsSpecializations")}</span>
            </div>

            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-6 w-2/3" />
                <Skeleton className="h-6 w-4/5" />
              </div>
            ) : bySubjectsData && Object.keys(bySubjectsData).length ? (
              <div className="divide-y divide-gray-100">
                {Object.keys(bySubjectsData).map((className) => {
                  const open = !!expandedClasses[className];
                  return (
                    <div key={className} className="py-3">
                      <button
                        className="w-full flex items-center justify-between text-left text-gray-800 font-medium hover:text-[#2BACE2]"
                        onClick={() => toggleClass(className)}
                      >
                        <span className="truncate">{className || t("studentDash.teacherProtfolio.general")}</span>
                        <span className="ml-3">
                          {open ? <IoChevronUp /> : <IoChevronDownOutline />}
                        </span>
                      </button>
                      <div
                        className="transition-all duration-300 ease-out overflow-hidden"
                        style={{ maxHeight: open ? 1000 : 0, opacity: open ? 1 : 0 }}
                      >
                        <ul className="mt-3 space-y-2">
                          {(bySubjectsData[className] || []).map((subject, idx) => (
                            <li
                              key={`${className}-${idx}`}
                              className="rounded-xl bg-gray-50 p-3 border border-gray-100"
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-semibold text-gray-800">
                                  {subject}
                                </span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-600">{t("studentDash.teacherProtfolio.noSpecializationsAvailable")}</p>
            )}
          </div>
        </div>
      </div>

      {/* SHARE MODAL */}
      {isShareModalOpen && (
        <div className="fixed inset-0 bg-gray-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-sm">
            <h2 className="text-lg font-semibold mb-4">{t("studentDash.homeTeacherPortfolio.shareThisProfile")}</h2>
            <div className="flex justify-between gap-3">
              <button
                onClick={() => handleShare("facebook")}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg grid place-items-center"
                aria-label={t("studentDash.teacherProtfolio.shareOnFacebook")}
              >
                <FaFacebook size={22} />
              </button>
              <button
                onClick={() => handleShare("whatsapp")}
                className="flex-1 bg-green-500 text-white py-2 rounded-lg grid place-items-center"
                aria-label={t("studentDash.teacherProtfolio.shareOnWhatsApp")}
              >
                <FaWhatsapp size={22} />
              </button>
              <button
                onClick={() => handleShare("linkedin")}
                className="flex-1 bg-blue-700 text-white py-2 rounded-lg grid place-items-center"
                aria-label={t("studentDash.teacherProtfolio.shareOnLinkedIn")}
              >
                <FaLinkedin size={22} />
              </button>
              <button
                onClick={handleInstagramShare}
                className="flex-1 bg-gray-600 text-white py-2 rounded-lg grid place-items-center"
                aria-label={t("studentDash.teacherProtfolio.copyProfileLink")}
              >
                <FaLink size={22} />
              </button>
            </div>
            <button
              onClick={() => setIsShareModalOpen(false)}
              className="mt-4 w-full text-gray-600 hover:text-gray-800 text-sm"
            >
              {t("studentDash.common.close")}
            </button>
          </div>
        </div>
      )}

      {/* REQUEST MODAL */}
      {showModal && (
        <>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="relative w-full max-w-md">
              <div className="rounded-2xl shadow-2xl bg-white overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b">
                  <h3 className="text-lg font-semibold">{t("studentDash.homeTeacherPortfolio.selectSlot")}</h3>
                  <button
                    className="text-2xl leading-none"
                    onClick={() => setShowModal(false)}
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
                  {/* Duration */}
                  <div className="flex flex-col items-center">
                    <select
                      name="session_id"
                      className="w-full md:w-2/3 text-sm border rounded-lg bg-[#F9FBFF] px-3 py-2 outline-none"
                      onChange={handleDurationChange}
                      value={formData.session_id}
                    >
                      <option value="">{t("studentDash.homeTeacherPortfolio.selectDuration")}</option>
                      {demoDuration.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.days} {d.duration}
                        </option>
                      ))}
                    </select>
                    {errorMessage && (
                      <p className="text-red-500 text-xs mt-1">{errorMessage}</p>
                    )}
                  </div>

                  {/* Time Slot */}
                  <div className="flex flex-col items-center">
                    {!isAlwaysAvailable ? (
                      <select
                        name="timeslot_id"
                        className="w-full md:w-2/3 text-sm border rounded-lg bg-[#F9FBFF] px-3 py-2 outline-none"
                        onChange={handleChange}
                        value={formData.timeslot_id}
                      >
                        <option value="">{t("studentDash.homeTeacherPortfolio.selectTimeSlot")}</option>
                        {byTimeSlotData.map((slot) => (
                          <option key={slot.id} value={slot.id}>
                            {slot.day_name}: {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="datetime-local"
                        name="timeslot_id"
                        className="w-full md:w-2/3 text-sm border rounded-lg bg-[#F9FBFF] px-3 py-2 outline-none"
                        onChange={handleChange}
                        value={formData.timeslot_id || ""}
                      />
                    )}
                    {errors.timeslot_id && (
                      <p className="text-red-500 text-xs mt-1">{t("studentDash.homeTeacherPortfolio.timeslotRequired")}</p>
                    )}
                  </div>

                  {/* Payment */}
                  <div className="flex flex-col items-center">
                    <input
                      type="text"
                      name="payment"
                      placeholder={t("studentDash.homeTeacherPortfolio.paymentPlaceholder")}
                      className="w-full md:w-2/3 text-sm border rounded-lg bg-[#F9FBFF] px-3 py-2 outline-none"
                      onChange={handleChange}
                      value={formData.payment}
                      disabled={!(formData.payment === "")}
                    />
                    {errors.payment && (
                      <p className="text-red-500 text-xs mt-1">{t("studentDash.homeTeacherPortfolio.paymentRequired")}</p>
                    )}
                  </div>

                  {/* Topic */}
                  <div className="flex flex-col items-center">
                    <input
                      type="text"
                      name="topic"
                      placeholder={t("studentDash.homeTeacherPortfolio.enterTopicPlaceholder")}
                      className="w-full md:w-2/3 text-sm border rounded-lg bg-[#F9FBFF] px-3 py-2 outline-none"
                      onChange={handleChange}
                      value={formData.topic}
                    />
                    {errors.topic && (
                      <p className="text-red-500 text-xs mt-1">{t("studentDash.homeTeacherPortfolio.topicRequired")}</p>
                    )}
                  </div>

                  <div className="pt-2 pb-5 text-center">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className={`px-10 py-2 rounded-xl text-white font-semibold  bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] focus:outline-none ${
                        isLoading ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      {isLoading ? (
                        <svg
                          className="animate-spin h-5 w-5 mx-auto"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      ) : (
                        t("studentDash.common.submit")
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          {/* backdrop */}
          <div
            className="fixed inset-0 bg-black/40"
            onClick={() => setShowModal(false)}
          />
        </>
      )}
    </div>
  );
};

export default TeacherPortfolio;
